import React from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import "./MissionPage.css";

/**
 * OUR MISSION — the heart of Everything Eco.
 * Built from Julio's master vision: every person, every item, every community,
 * and every opportunity has value. Second chances. Honesty as a strength.
 */

const CHANCES = [
  { icon: "♻️", title: "Materials", text: "Reused instead of wasted. Plastic, metal, cardboard — back into the loop, not the landfill." },
  { icon: "💻", title: "Electronics", text: "Repaired, refurbished, and given a new purpose — like the laptops on our workbench right now." },
  { icon: "🏙️", title: "Neighborhoods", text: "Cleaner streets, stronger blocks, and local partnerships that keep value in the community." },
  { icon: "🤝", title: "People", text: "Education, opportunity, volunteering, and dignity. People are never disposable." },
];

const VALUE_CHIPS = ["People", "Communities", "Electronics", "Furniture", "Ideas", "Neighborhoods", "Businesses", "Opportunities"];

const FLOW = [
  { icon: "🏠", title: "Pickup", text: "We come to you — free." },
  { icon: "📦", title: "Sorting", text: "Every item checked for its next life." },
  { icon: "🔧", title: "Repair & refurbish", text: "Whatever can be saved, gets saved." },
  { icon: "❤️ / ♻️", title: "Donated or recycled", text: "To a neighbor in need — or responsibly recycled." },
  { icon: "🌎", title: "Impact you can see", text: "Your items keep creating value after they leave your home." },
];

const HERO_LINES = ["Every person.", "Every item.", "Every community.", "Every opportunity."];

const MissionPage: React.FC = () => {
  useReveal();
  return (
    <div className="mp-page">
      {/* ── Hero ── */}
      <section className="mp-hero">
        <div className="mp-hero-orb o1" /><div className="mp-hero-orb o2" /><div className="mp-hero-orb o3" />
        <div className="ee-container mp-hero-inner">
          <div className="mp-kicker">🌎 OUR MISSION</div>
          <h1 className="mp-h1">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="mp-h1-line" style={{ animationDelay: `${0.15 + i * 0.18}s` }}>{line}</span>
            ))}
            <span className="mp-h1-value" style={{ animationDelay: `${0.15 + HERO_LINES.length * 0.18 + 0.1}s` }}>HAS VALUE.</span>
          </h1>
          <p className="mp-hero-sub">
            Everything Eco exists to prove that every person, every item, every community, and every
            opportunity has value. Protecting the planet and improving people's lives go hand in hand.
          </p>
          <div className="mp-hero-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green">📦 Book a Free Pickup</Link>
            <a href="#movement" className="ee-btn mp-btn-ghost">Why we exist ↓</a>
          </div>
        </div>
        <div className="mp-scroll-cue">▾</div>
      </section>

      {/* ── The movement ── */}
      <section id="movement" className="ee-section mp-movement">
        <div className="ee-container mp-narrow">
          <div className="ee-section-label">Why we exist</div>
          <h2 className="ee-section-title">We're not building a recycling company.<br />We're building a movement.</h2>
          <p>
            We believe that protecting the planet and improving people's lives go hand in hand. By combining
            environmental action, technology, education, and community service, we're building more than a
            recycling company — we're building a movement where everyone has the opportunity to make a
            positive impact.
          </p>
          <p>
            Everything Eco is proudly starting as a small, community-driven business with a big vision.
            Every pickup, every donation, every volunteer, every partnership, and every person who joins us
            moves that vision forward.
          </p>
          <p className="mp-strong-line">
            Small actions, multiplied by thousands of people, create lasting change.
          </p>
        </div>
      </section>

      {/* ── Second chances ── */}
      <section className="ee-section mp-chances">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>What we really do</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>
            We don't just recycle things.<br />
            <span className="mp-grad">We create second chances.</span>
          </h2>
          <div className="mp-chance-grid">
            {CHANCES.map((c, i) => (
              <div key={c.title} className="mp-chance-card" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="mp-chance-ico">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Philosophy band ── */}
      <section className="ee-section mp-philosophy">
        <div className="ee-container mp-narrow" style={{ textAlign: "center" }}>
          <div className="mp-phil-kicker">OUR PHILOSOPHY</div>
          <p className="mp-phil-quote">
            “Value exists everywhere. Sometimes it simply needs someone <em>willing to recognize it</em>.”
          </p>
          <div className="mp-chip-row">
            {VALUE_CHIPS.map((c) => <span key={c} className="mp-chip">{c}</span>)}
          </div>
        </div>
      </section>

      {/* ── After a pickup ── */}
      <section className="ee-section mp-flow">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>Transparency</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>What happens after a pickup</h2>
          <div className="mp-flow-row">
            {FLOW.map((f, i) => (
              <React.Fragment key={f.title}>
                <div className="mp-flow-step">
                  <div className="mp-flow-ico">{f.icon}</div>
                  <strong>{f.title}</strong>
                  <span>{f.text}</span>
                </div>
                {i < FLOW.length - 1 && <div className="mp-flow-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Honesty ── */}
      <section className="ee-section mp-honest">
        <div className="ee-container mp-honest-card">
          <div className="mp-honest-emoji">🌱</div>
          <div>
            <h2>We're just getting started — and we're honest about it.</h2>
            <p>
              We're a small business. We're learning, growing, and improving every day — and we're building
              Everything Eco alongside our community, not above it. We aren't pretending to already be a huge
              company. We want you to <strong>grow with us</strong>. That honesty is one of our biggest strengths.
            </p>
            <div className="mp-honest-btns">
              <Link to="/vision" className="ee-btn ee-btn-green">🔭 See the full vision</Link>
              <Link to="/vision" className="ee-btn mp-btn-outline">Our roadmap</Link>
              <Link to="/contact" className="ee-btn mp-btn-outline">Build with us →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── North star ── */}
      <section className="ee-section mp-north">
        <div className="ee-container mp-narrow" style={{ textAlign: "center" }}>
          <div className="mp-north-star">✦</div>
          <p className="mp-north-quote">
            We aren't trying to build the biggest recycling company. We're trying to build one of the most
            impactful <span className="mp-grad">community-driven environmental organizations</span> in the world.
          </p>
          <p className="mp-north-sign">— Julio Ramirez, Founder</p>
        </div>
      </section>

      {/* ── Join ── */}
      <section className="ee-section mp-join">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <h2 className="ee-section-title">Join the movement 💚</h2>
          <p className="mp-join-sub">Recycling is our foundation. People are our purpose.</p>
          <div className="mp-join-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green">📦 Schedule a Free Pickup</Link>
            <Link to="/contact" className="ee-btn ee-btn-blue">🤝 Volunteer or Partner</Link>
            <Link to="/game" className="ee-btn mp-btn-outline">🎮 Meet Earth Jr.</Link>
          </div>
          <a
            className="mp-insta"
            href="https://instagram.com/everythingeconyc"
            target="_blank"
            rel="noreferrer"
          >
            📸 Follow the journey — @everythingeconyc
          </a>
        </div>
      </section>
    </div>
  );
};

export default MissionPage;
